import { useState } from "react";
import { BridgeKit } from "@circle-fin/bridge-kit";
import { createAdapterFromProvider } from "@circle-fin/adapter-viem-v2";
import { BRIDGEKIT_CHAIN_NAMES } from "../utils/constants";
import { switchToChain, getChainConfig } from "../utils/addNetwork";

export const useTransfer = (
  walletClient,
  publicClients,
  switchChain,
  chain,
  sourceChainId,
  destinationChainId
) => {
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [txHashes, setTxHashes] = useState({ approve: "", burn: "", mint: "" });

  // Initialize BridgeKit
  const kit = new BridgeKit();

  /**
   * Resolve the EIP-1193 provider from the wagmi walletClient (MetaMask)
   * or fall back to window.ethereum if wagmi hasn't hydrated yet.
   */
  const resolveProvider = () => {
    // walletClient.transport is the EIP-1193 provider wagmi is using (MetaMask)
    if (walletClient?.transport) {
      return walletClient.transport;
    }
    // Fallback: use raw window.ethereum (MetaMask injects this)
    if (typeof window !== "undefined" && window.ethereum) {
      return window.ethereum;
    }
    throw new Error("No wallet provider found. Please connect MetaMask.");
  };

  const executeTransfer = async (amount, maxFee, destinationAddress) => {
    try {
      setLoading(true);
      setError("");
      setStatus("Initializing transfer...");

      // Ensure we're on the source chain
      if (chain?.id !== sourceChainId) {
        setStatus("Switching to source chain...");
        const chainConfig = getChainConfig(sourceChainId);
        await switchToChain(switchChain, sourceChainId, chainConfig);
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }

      const provider = resolveProvider();
      const adapter = await createAdapterFromProvider({ provider });

      // Determine chain names for BridgeKit
      const sourceChainName = BRIDGEKIT_CHAIN_NAMES[sourceChainId] || "Unknown";
      const destinationChainName = BRIDGEKIT_CHAIN_NAMES[destinationChainId] || "Unknown";

      setStatus(
        `Bridging ${amount} USDC from ${sourceChainName} to ${destinationChainName}...`
      );

      // Execute bridge using BridgeKit
      console.log({
        from: { adapter, chain: sourceChainName },
        to: { adapter, chain: destinationChainName },
        amount: amount,
      });

      const result = await kit.bridge({
        from: { adapter, chain: sourceChainName },
        to: { adapter, chain: destinationChainName },
        amount: amount,
        config: { transferSpeed: "FAST" },
      });
      console.log(result);

      // Extract transaction hashes from result
      if (result?.approvalTxHash) {
        setTxHashes((prev) => ({ ...prev, approve: result.approvalTxHash }));
        setStatus("✓ USDC Approved!");
      }

      if (result?.burnTxHash) {
        setTxHashes((prev) => ({ ...prev, burn: result.burnTxHash }));
        setStatus("✓ USDC Burned!");
      }

      if (result?.mintTxHash) {
        setTxHashes((prev) => ({ ...prev, mint: result.mintTxHash }));
        setStatus("✓ USDC Minted!");
      }

      setStatus("✅ Transfer completed successfully!");
      setLoading(false);
    } catch (err) {
      console.error("Transfer failed:", err);
      const message = err.message || "Transfer failed";
      setError(message);
      setLoading(false);
    }
  };

  return {
    status,
    loading,
    error,
    txHashes,
    executeTransfer,
  };
};
